/**
 * SceneTransition — visual transition runner for the painted-scene
 * journey (AGE-1421).
 *
 * Plugs into AGE-1422's `SceneRouter` via the `SceneTransitionRunner`
 * interface. The router handles persistence + state; this module handles
 * what the player actually *sees* between two `PaintedScene` instances:
 *
 *   - edge-crossfade — walker walks off a frame edge; B fades in over A.
 *                      Walker continuity is preserved because B's spawn
 *                      is set on the new scene's init data and the
 *                      crossfade hides the snap.
 *   - doorway        — camera dollies + zooms toward the door anchor; a
 *                      full-screen black vignette opens, the scene swaps
 *                      under it, vignette closes; B's walker steps
 *                      forward briefly.
 *   - camera-pan     — A and B render side-by-side; the source scene's
 *                      viewport slides off-canvas to the right while the
 *                      target slides in from the right.
 *   - wash           — fallback (currently aliased to edge-crossfade with
 *                      a longer duration); shader-driven noise dissolve
 *                      lands in a follow-up phase.
 *
 * Architecture
 * ------------
 * The runner does not own scenes. It looks up the running source scene by
 * manifest key, launches the target scene in parallel (so both render
 * during the transition), tweens visuals on top of both, and stops the
 * source scene at the end. CaptureFrame is intentionally not used —
 * running both `PaintedScene`s simultaneously is cheaper and keeps the
 * walker animations live in the target during the fade-in.
 *
 * Audio crossfade rides the same eased timeline so audio + visual feel
 * coupled. The music key is read from each scene's manifest entry; if a
 * music asset isn't loaded the crossfader silently no-ops (the
 * framework-test scene runs with no audio assets and the spec's "no
 * clicks/pops" guarantee still holds — silence has no clicks).
 */

import * as Phaser from 'phaser';
import type {
  TransitionKind,
  NormalisedPoint,
  PaintedSceneSpec,
} from './journeyManifest.schema.js';
import {
  cameraPanScrollX,
  type CrossfadeSound,
  doorwayCameraTarget,
  doorwayVignetteAlpha,
  easeInOutCubic,
  edgeCrossfadeAlphas,
  edgePanCameraTarget,
  MusicCrossfader,
  resolveDuration,
  thresholdZoomCameraTarget,
  thresholdZoomFrameAlpha,
  thresholdZoomMaskRect,
} from './sceneTransitionState.js';

// Re-export so consumers can import `MusicCrossfader` from this module —
// keeps the runner the canonical entry point even though the
// implementation lives with the rest of the Phaser-free helpers.
export { MusicCrossfader };
import { resolveRegionKey } from '../../audio/paintedSceneMusic.js';
import {
  PaintedScene,
  PAINTED_SCENE_EVENTS,
  type PaintedSceneInitData,
} from './PaintedScene.js';
import type {
  SceneTransitionRequest,
  SceneTransitionRunner,
} from './SceneRouter.js';

/** Phaser scene key for the transition overlay (vignette, debug). */
export const TRANSITION_OVERLAY_SCENE_KEY = 'PaintedSceneTransitionOverlay';

/**
 * Logical canvas size painted scenes are authored against. Mirrors the
 * `PAINTING_DESIGN_W/H` in the gallery viewer; redeclared here so this
 * module doesn't reach across feature boundaries.
 */
const PAINTING_CANVAS_W = 1344;

// PaintedScene field accessors — `manifestKey`, `manifestEntry`, and
// `ready` are `protected` on the base class, but the runner needs to read
// them to wire up the visual transition. Casting through `unknown` once,
// here, is cleaner than salting the call sites with bracket-access tricks.
//
// If PaintedScene grows public accessors later these helpers can collapse
// to direct property reads.

interface PaintedSceneSurface {
  manifestKey: string;
  manifestEntry: PaintedSceneSpec | null;
  ready: boolean;
}

function paintedSurface(scene: PaintedScene): PaintedSceneSurface {
  return scene as unknown as PaintedSceneSurface;
}

function paintedManifestKey(scene: PaintedScene): string {
  return paintedSurface(scene).manifestKey;
}

function paintedManifestEntry(scene: PaintedScene): PaintedSceneSpec | null {
  return paintedSurface(scene).manifestEntry;
}

function isPaintedSceneReady(scene: PaintedScene): boolean {
  return paintedSurface(scene).ready === true;
}

/**
 * Transition overlay scene — a top-of-stack scene that renders a vignette
 * and (during doorway transitions) a full-screen black mask. Always
 * launched on demand by the runner and stopped at the end of the
 * transition. No persistent state; idempotent restarts are fine.
 */
export class PaintedSceneTransitionOverlay extends Phaser.Scene {
  public static readonly KEY = TRANSITION_OVERLAY_SCENE_KEY;

  private vignette: Phaser.GameObjects.Rectangle | null = null;
  private doorFrame: Phaser.GameObjects.Graphics | null = null;

  constructor() {
    super({ key: TRANSITION_OVERLAY_SCENE_KEY });
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.vignette = this.add
      .rectangle(0, 0, w, h, 0x000000, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(1000);

    this.doorFrame = this.add.graphics();
    this.doorFrame.setScrollFactor(0).setDepth(1001).setAlpha(0);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.vignette = null;
      this.doorFrame = null;
    });
  }

  setVignetteAlpha(a: number): void {
    if (this.vignette) this.vignette.setAlpha(a);
  }

  /**
   * Draw a door-frame mask: full-screen opaque black with a transparent
   * rectangular hole where the door opening is. The hole expands over time
   * to reveal the scene underneath.
   */
  setDoorFrameMask(
    holeRect: { x: number; y: number; w: number; h: number },
    alpha: number,
  ): void {
    if (!this.doorFrame) return;
    const w = this.scale.width;
    const h = this.scale.height;

    this.doorFrame.clear();
    this.doorFrame.setAlpha(alpha);

    if (alpha <= 0) return;

    this.doorFrame.fillStyle(0x000000, 1);

    const hx = holeRect.x;
    const hy = holeRect.y;
    const hw = holeRect.w;
    const hh = holeRect.h;

    if (hy > 0) this.doorFrame.fillRect(0, 0, w, hy);
    if (hy + hh < h) this.doorFrame.fillRect(0, hy + hh, w, h - (hy + hh));
    if (hx > 0) this.doorFrame.fillRect(0, hy, hx, hh);
    if (hx + hw < w) this.doorFrame.fillRect(hx + hw, hy, w - (hx + hw), hh);
  }

  clearDoorFrame(): void {
    if (!this.doorFrame) return;
    this.doorFrame.clear();
    this.doorFrame.setAlpha(0);
  }
}

/**
 * Bookkeeping for a single transition run.
 */
interface ActiveRun {
  req: SceneTransitionRequest;
  resolve: () => void;
  durationMs: number;
}

/**
 * `SceneTransitionRunner` impl that drives the visual choreography on top
 * of two running `PaintedScene` instances.
 *
 * Construct it with the live `Phaser.Game` and a manifest-key → Phaser-key
 * map (or rely on automatic lookup over `scene.manager.scenes`). Pass it
 * to `SceneRouter` as `transition: new PaintedSceneTransitionRunner(game)`.
 */
export class PaintedSceneTransitionRunner implements SceneTransitionRunner {
  private readonly crossfader = new MusicCrossfader();
  private busy = false;
  /** Optional explicit lookup table when scenes aren't yet running. */
  private readonly manifestToPhaser: ReadonlyMap<string, string>;

  constructor(
    private readonly game: Phaser.Game,
    opts: {
      manifestToPhaserKey?: ReadonlyMap<string, string> | Record<string, string>;
    } = {},
  ) {
    if (opts.manifestToPhaserKey instanceof Map) {
      this.manifestToPhaser = opts.manifestToPhaserKey;
    } else if (opts.manifestToPhaserKey) {
      this.manifestToPhaser = new Map(
        Object.entries(opts.manifestToPhaserKey),
      );
    } else {
      this.manifestToPhaser = new Map();
    }
  }

  /** True while a transition is in flight. */
  isBusy(): boolean {
    return this.busy;
  }

  /**
   * Run the visual choreography for `req` and resolve once the scene has
   * settled on the target. If a transition is already in flight the call
   * resolves immediately with no change (router already gates this, but
   * the runner double-checks so a buggy caller can't stack two fades).
   */
  async run(req: SceneTransitionRequest): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const durationMs = resolveDuration(req.kind, req.durationMs);
      await this.dispatch(req, durationMs);
    } finally {
      this.busy = false;
    }
  }

  // ── dispatch ────────────────────────────────────────────────────────────

  private async dispatch(
    req: SceneTransitionRequest,
    durationMs: number,
  ): Promise<void> {
    const fromPhaserKey = req.fromScene
      ? this.resolvePhaserKey(req.fromScene)
      : null;
    const toPhaserKey = this.resolvePhaserKey(req.toScene);
    if (!toPhaserKey) {
      console.warn(
        `[SceneTransition] no Phaser scene registered for "${req.toScene}"; skipping transition`,
      );
      return;
    }
    const fromScene = fromPhaserKey
      ? this.game.scene.getScene(fromPhaserKey)
      : null;
    if (!fromScene) {
      // No source scene running — nothing to transition from; just start
      // the target (e.g. fresh boot / first navigation).
      this.startTarget(toPhaserKey, req);
      return;
    }

    // Launch the overlay (idempotent; a previous run may have torn it down).
    this.ensureOverlayLaunched(fromScene);

    switch (req.kind) {
      case 'edge-crossfade':
        await this.runEdgeCrossfade(fromScene, toPhaserKey, req, durationMs);
        break;
      case 'doorway':
        await this.runDoorway(fromScene, toPhaserKey, req, durationMs);
        break;
      case 'threshold-zoom':
        await this.runThresholdZoom(fromScene, toPhaserKey, req, durationMs);
        break;
      case 'camera-pan':
        await this.runCameraPan(fromScene, toPhaserKey, req, durationMs);
        break;
      case 'edge-pan':
        await this.runEdgePan(fromScene, toPhaserKey, req);
        break;
      case 'wash':
        // Wash is the catch-all painterly fallback. Phase β of the
        // round adds the shader-driven noise dissolve; today it's an
        // alpha crossfade with a slightly longer duration.
        await this.runEdgeCrossfade(fromScene, toPhaserKey, req, durationMs);
        break;
    }
  }

  // ── per-kind choreography ───────────────────────────────────────────────

  private async runEdgeCrossfade(
    fromScene: Phaser.Scene,
    toPhaserKey: string,
    req: SceneTransitionRequest,
    durationMs: number,
  ): Promise<void> {
    const targetScene = await this.launchTargetParallel(
      toPhaserKey,
      req,
      fromScene,
    );
    if (!targetScene) return;

    // Stack target above source (later in scene list = on top).
    this.bringSceneToTop(toPhaserKey);
    this.bringOverlayToTop();

    targetScene.cameras.main.setAlpha(0);
    fromScene.cameras.main.setAlpha(1);

    const incomingMusic = this.musicSoundOf(targetScene);
    const outgoingMusic = this.musicSoundOf(fromScene);

    await this.runTimeline(fromScene, durationMs, (elapsed) => {
      const t = Math.min(1, elapsed / durationMs);
      const alphas = edgeCrossfadeAlphas(t);
      fromScene.cameras.main.setAlpha(alphas.outgoing);
      targetScene.cameras.main.setAlpha(alphas.incoming);
      this.crossfader.applyCrossfade(outgoingMusic, incomingMusic, t);
    });

    targetScene.cameras.main.setAlpha(1);
    this.crossfader.finishCrossfade(outgoingMusic, incomingMusic);
    this.stopScene(fromScene.scene.key);
    this.stopOverlay();
  }

  private async runDoorway(
    fromScene: Phaser.Scene,
    toPhaserKey: string,
    req: SceneTransitionRequest,
    durationMs: number,
  ): Promise<void> {
    const overlay = this.getOverlay();
    const door = this.findDoorAnchor(req) ?? { x: 0.5, y: 0.5 };

    const cam = fromScene.cameras.main;
    const startScrollX = cam.scrollX;
    const startScrollY = cam.scrollY;
    const startZoom = cam.zoom;

    const w = fromScene.scale.width;
    const h = fromScene.scale.height;

    // Phase 1: dolly + zoom toward the door under cover of the source scene.
    // The vignette ramps up; we don't launch the target yet — it lives behind
    // the black peak so the swap is visually invisible.
    const prepareMs = durationMs * 0.4;
    const swapMs = durationMs * 0.4;
    const settleMs = durationMs * 0.2;

    const incomingMusic: { sound: CrossfadeSound | null } = { sound: null };
    const outgoingMusic = this.musicSoundOf(fromScene);

    // Phase 1: prepare.
    await this.runTimeline(fromScene, prepareMs, (elapsed) => {
      const phaseT = Math.min(1, elapsed / prepareMs);
      const target = doorwayCameraTarget(door, phaseT, w, h);
      cam.setZoom(target.zoom);
      cam.centerOn(target.centerX, target.centerY);
      // Vignette alpha for the prepare-phase only goes up to the apex.
      const t = phaseT * 0.5; // 0 → 0.5 maps prepare into the rising vignette.
      overlay?.setVignetteAlpha(doorwayVignetteAlpha(t));
      this.crossfader.applyCrossfade(outgoingMusic, null, phaseT * 0.5);
    });

    // Phase 2: swap. Target launches under cover of opaque vignette.
    const targetScene = await this.launchTargetParallel(
      toPhaserKey,
      req,
      fromScene,
    );
    if (!targetScene) {
      // Restore source on failure.
      cam.setZoom(startZoom);
      cam.setScroll(startScrollX, startScrollY);
      overlay?.setVignetteAlpha(0);
      this.stopOverlay();
      return;
    }
    incomingMusic.sound = this.musicSoundOf(targetScene);
    this.bringSceneToTop(toPhaserKey);
    this.bringOverlayToTop();

    // Reset source camera (it'll be torn down at the tail) and ensure the
    // target's camera starts at default.
    cam.setZoom(1);
    cam.setScroll(0, 0);
    fromScene.cameras.main.setAlpha(1);
    targetScene.cameras.main.setAlpha(1);

    // Hold the vignette opaque during the swap window — covers the camera
    // reset and the source-scene tear-down.
    await this.wait(fromScene, swapMs * 0.5);

    // Phase 3: settle. Vignette opens back up over the target scene.
    this.stopScene(fromScene.scene.key);
    await this.runTimeline(targetScene, swapMs * 0.5 + settleMs, (elapsed) => {
      const phaseT = Math.min(1, elapsed / (swapMs * 0.5 + settleMs));
      const t = 0.5 + phaseT * 0.5; // 0.5 → 1.0 maps settle into the falling vignette.
      overlay?.setVignetteAlpha(doorwayVignetteAlpha(t));
      this.crossfader.applyCrossfade(outgoingMusic, incomingMusic.sound, 0.5 + phaseT * 0.5);
    });

    overlay?.setVignetteAlpha(0);
    this.crossfader.finishCrossfade(outgoingMusic, incomingMusic.sound);
    this.stopOverlay();
  }

  private async runThresholdZoom(
    fromScene: Phaser.Scene,
    toPhaserKey: string,
    req: SceneTransitionRequest,
    durationMs: number,
  ): Promise<void> {
    const overlay = this.getOverlay();
    const door = this.findDoorAnchor(req) ?? { x: 0.5, y: 0.5 };
    const doorRect = this.findDoorRect(req) ?? { x: 0.4, y: 0.3, w: 0.2, h: 0.2 };
    const peakZoom = 2.5;

    const cam = fromScene.cameras.main;
    const startScrollX = cam.scrollX;
    const startScrollY = cam.scrollY;
    const startZoom = cam.zoom;

    const w = fromScene.scale.width;
    const h = fromScene.scale.height;

    const prepareMs = durationMs * 0.4;
    const crossfadeMs = durationMs * 0.4;
    const settleMs = durationMs * 0.2;

    const incomingMusic: { sound: CrossfadeSound | null } = { sound: null };
    const outgoingMusic = this.musicSoundOf(fromScene);

    // Phase 1: camera dollies toward door, zooming in until the door fills
    // the screen. The door-frame mask fades in around the door opening.
    await this.runTimeline(fromScene, prepareMs, (elapsed) => {
      const phaseT = Math.min(1, elapsed / prepareMs);
      const target = thresholdZoomCameraTarget(door, phaseT, w, h, peakZoom);
      cam.setZoom(target.zoom);
      cam.centerOn(target.centerX, target.centerY);
      const frameAlpha = thresholdZoomFrameAlpha(phaseT * 0.4);
      overlay?.setDoorFrameMask(
        thresholdZoomMaskRect(doorRect, door, 0, w, h, peakZoom),
        frameAlpha,
      );
      this.crossfader.applyCrossfade(outgoingMusic, null, phaseT * 0.3);
    });

    // Phase 2: launch target scene. The door-frame mask hides everything
    // outside the door opening; the incoming scene is visible through the
    // hole. The hole gradually expands while the source fades out.
    const targetScene = await this.launchTargetParallel(
      toPhaserKey,
      req,
      fromScene,
    );
    if (!targetScene) {
      cam.setZoom(startZoom);
      cam.setScroll(startScrollX, startScrollY);
      overlay?.clearDoorFrame();
      this.stopOverlay();
      return;
    }
    incomingMusic.sound = this.musicSoundOf(targetScene);

    // Target renders behind source; the door opening is the window through.
    // Bring source on top so it serves as the "frame" around the opening.
    this.bringSceneToTop(fromScene.scene.key);
    this.bringOverlayToTop();
    targetScene.cameras.main.setAlpha(1);

    await this.runTimeline(fromScene, crossfadeMs, (elapsed) => {
      const phaseT = Math.min(1, elapsed / crossfadeMs);
      const globalT = 0.4 + phaseT * 0.4;
      // Source scene fades out; the door-frame mask expands the hole.
      fromScene.cameras.main.setAlpha(1 - easeInOutCubic(phaseT));
      overlay?.setDoorFrameMask(
        thresholdZoomMaskRect(doorRect, door, phaseT, w, h, peakZoom),
        thresholdZoomFrameAlpha(globalT),
      );
      this.crossfader.applyCrossfade(outgoingMusic, incomingMusic.sound, 0.3 + phaseT * 0.5);
    });

    // Phase 3: settle. Source is gone, mask clears, target scene is fully
    // visible. Brief settle for camera/audio landing.
    this.stopScene(fromScene.scene.key);
    overlay?.clearDoorFrame();

    await this.runTimeline(targetScene, settleMs, (elapsed) => {
      const phaseT = Math.min(1, elapsed / settleMs);
      this.crossfader.applyCrossfade(outgoingMusic, incomingMusic.sound, 0.8 + phaseT * 0.2);
    });

    this.crossfader.finishCrossfade(outgoingMusic, incomingMusic.sound);
    this.stopOverlay();
  }

  private async runCameraPan(
    fromScene: Phaser.Scene,
    toPhaserKey: string,
    req: SceneTransitionRequest,
    durationMs: number,
  ): Promise<void> {
    const targetScene = await this.launchTargetParallel(
      toPhaserKey,
      req,
      fromScene,
    );
    if (!targetScene) return;

    const w = fromScene.scale.width;
    const fromCam = fromScene.cameras.main;
    const toCam = targetScene.cameras.main;

    // Position the target's camera viewport to the right of the canvas so
    // it slides in from off-screen as the source slides off to the left.
    fromCam.setViewport(0, 0, w, fromScene.scale.height);
    toCam.setViewport(w, 0, w, targetScene.scale.height);

    this.bringSceneToTop(toPhaserKey);
    this.bringOverlayToTop();

    const incomingMusic = this.musicSoundOf(targetScene);
    const outgoingMusic = this.musicSoundOf(fromScene);

    await this.runTimeline(fromScene, durationMs, (elapsed) => {
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);
      // Source slides left off-canvas; target slides in from the right.
      const offset = cameraPanScrollX(t, w);
      fromCam.setViewport(-offset, 0, w, fromScene.scale.height);
      toCam.setViewport(w - offset, 0, w, targetScene.scale.height);
      // Use eased so the scroll feels coupled to the same curve as audio.
      void eased;
      this.crossfader.applyCrossfade(outgoingMusic, incomingMusic, t);
    });

    // Restore the target's viewport to the canvas origin and tear down source.
    toCam.setViewport(0, 0, w, targetScene.scale.height);
    this.crossfader.finishCrossfade(outgoingMusic, incomingMusic);
    this.stopScene(fromScene.scene.key);
    this.stopOverlay();
  }

  private async runEdgePan(
    fromScene: Phaser.Scene,
    toPhaserKey: string,
    req: SceneTransitionRequest,
  ): Promise<void> {
    const targetScene = await this.launchTargetParallel(
      toPhaserKey,
      req,
      fromScene,
    );
    if (!targetScene) return;

    const fromSpec = this.manifestSpecOf(fromScene);
    const toSpec = this.manifestSpecOf(targetScene);

    const fromOrigin = fromSpec?.worldOrigin ?? { x: 0, y: 0 };
    const toOrigin = toSpec?.worldOrigin ?? { x: 0, y: 0 };

    const toCam = targetScene.cameras.main;
    toCam.setScroll(
      toCam.scrollX + (toOrigin.x - fromOrigin.x),
      toCam.scrollY + (toOrigin.y - fromOrigin.y),
    );

    this.bringSceneToTop(toPhaserKey);
    this.bringOverlayToTop();

    const w = fromScene.scale.width;
    const h = fromScene.scale.height;
    const spawnWorldX = toOrigin.x + req.spawn.x * w;
    const spawnWorldY = toOrigin.y + req.spawn.y * h;

    const cam = fromScene.cameras.main;
    const { scrollX, scrollY } = edgePanCameraTarget(
      spawnWorldX,
      spawnWorldY,
      w,
      h,
    );
    cam.setScroll(scrollX, scrollY);

    const incomingMusic = this.musicSoundOf(targetScene);
    const outgoingMusic = this.musicSoundOf(fromScene);
    this.crossfader.finishCrossfade(outgoingMusic, incomingMusic);

    this.stopOverlay();
  }

  // ── scene plumbing ──────────────────────────────────────────────────────

  /**
   * Launch the target scene in parallel and resolve once it has emitted
   * `PaintedScene.READY` (or after a short timeout — better to start the
   * fade visibly behind a half-loaded scene than freeze the user on a
   * black canvas waiting for assets to arrive).
   *
   * `launcher` is the source scene whose ScenePlugin we use to issue the
   * launch (Phaser's per-scene plugin is the public surface for cross-
   * scene launches).
   */
  private async launchTargetParallel(
    phaserKey: string,
    req: SceneTransitionRequest,
    launcher: Phaser.Scene,
  ): Promise<Phaser.Scene | null> {
    const initData: PaintedSceneInitData = {
      spawn: req.spawn,
      ...(req.fromScene ? { fromScene: req.fromScene } : {}),
    };
    // If the scene is already running (e.g. the router fired back-to-back
    // requests), reuse it.
    const existing = this.game.scene.getScene(phaserKey);
    if (existing && this.game.scene.isActive(phaserKey)) {
      return existing;
    }
    if (!existing) {
      console.warn(`[SceneTransition] scene "${phaserKey}" not registered`);
      return null;
    }
    launcher.scene.launch(phaserKey, initData);
    const target = this.game.scene.getScene(phaserKey);
    if (!target) return null;

    // Wait for PaintedScene.READY — emitted at the end of `create()`.
    await this.waitForReady(target, launcher, 1500);
    return target;
  }

  private startTarget(phaserKey: string, req: SceneTransitionRequest): void {
    const target = this.game.scene.getScene(phaserKey);
    if (!target) {
      console.warn(`[SceneTransition] scene "${phaserKey}" not registered`);
      return;
    }
    const initData: PaintedSceneInitData = {
      spawn: req.spawn,
      ...(req.fromScene ? { fromScene: req.fromScene } : {}),
    };
    target.scene.start(phaserKey, initData);
  }

  /**
   * Wait for a scene to fire `PAINTED_SCENE_EVENTS.READY`. Resolves
   * immediately if the scene is already ready (PaintedScene sets
   * `ready = true` before emitting). The promise also resolves on
   * `timeoutMs` so a stuck preload can't deadlock the transition.
   */
  private waitForReady(
    targetScene: Phaser.Scene,
    timerScene: Phaser.Scene,
    timeoutMs: number,
  ): Promise<void> {
    if (
      targetScene instanceof PaintedScene &&
      targetScene.scene.isActive() &&
      isPaintedSceneReady(targetScene)
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        targetScene.events.off(PAINTED_SCENE_EVENTS.READY, onReady);
        resolve();
      };
      const onReady = (): void => settle();
      targetScene.events.on(PAINTED_SCENE_EVENTS.READY, onReady);
      timerScene.time.delayedCall(timeoutMs, settle);
    });
  }

  /** Bring a scene to the top of the render stack. */
  private bringSceneToTop(phaserKey: string): void {
    const scene = this.game.scene.getScene(phaserKey);
    if (scene) this.game.scene.bringToTop(scene);
  }

  /** Stop a scene and clean up its display list. */
  private stopScene(phaserKey: string): void {
    const scene = this.game.scene.getScene(phaserKey);
    if (!scene) return;
    if (scene.cameras && scene.cameras.main) {
      scene.cameras.main.setAlpha(1);
      scene.cameras.main.setZoom(1);
      scene.cameras.main.setScroll(0, 0);
      scene.cameras.main.setViewport(0, 0, scene.scale.width, scene.scale.height);
    }
    this.game.scene.stop(phaserKey);
  }

  /** Look up the running overlay scene, or null if not launched. */
  private getOverlay(): PaintedSceneTransitionOverlay | null {
    const s = this.game.scene.getScene(TRANSITION_OVERLAY_SCENE_KEY);
    return (s as PaintedSceneTransitionOverlay | null) ?? null;
  }

  private ensureOverlayLaunched(launcher: Phaser.Scene): void {
    if (!this.game.scene.getScene(TRANSITION_OVERLAY_SCENE_KEY)) {
      // Overlay scene is registered in the GameConfig; this branch is a
      // soft warning if config drift removed it.
      console.warn(
        `[SceneTransition] overlay scene "${TRANSITION_OVERLAY_SCENE_KEY}" not registered in GameConfig`,
      );
      return;
    }
    if (!this.game.scene.isActive(TRANSITION_OVERLAY_SCENE_KEY)) {
      launcher.scene.launch(TRANSITION_OVERLAY_SCENE_KEY);
    }
    this.bringOverlayToTop();
  }

  private bringOverlayToTop(): void {
    const s = this.game.scene.getScene(TRANSITION_OVERLAY_SCENE_KEY);
    if (s) this.game.scene.bringToTop(s);
  }

  private stopOverlay(): void {
    const overlay = this.getOverlay();
    if (overlay) overlay.setVignetteAlpha(0);
    if (this.game.scene.isActive(TRANSITION_OVERLAY_SCENE_KEY)) {
      this.game.scene.stop(TRANSITION_OVERLAY_SCENE_KEY);
    }
  }

  // ── helpers ─────────────────────────────────────────────────────────────

  /**
   * Resolve a manifest scene key to its Phaser scene key. Looks up the
   * explicit map first; falls back to scanning `scene.manager.scenes` for
   * a `PaintedScene` instance with a matching `manifestKey`.
   */
  private resolvePhaserKey(manifestKey: string): string | null {
    const fromMap = this.manifestToPhaser.get(manifestKey);
    if (fromMap) return fromMap;
    for (const s of this.game.scene.scenes) {
      if (s instanceof PaintedScene && paintedManifestKey(s) === manifestKey) {
        return s.scene.key;
      }
    }
    return null;
  }

  private manifestSpecOf(scene: Phaser.Scene): PaintedSceneSpec | null {
    if (!(scene instanceof PaintedScene)) return null;
    return paintedManifestEntry(scene);
  }

  /**
   * Try to find a music sound on the scene's sound manager keyed by the
   * scene's manifest entry. Returns null if the scene isn't a PaintedScene
   * or if no music is playing under that key.
   */
  private musicSoundOf(scene: Phaser.Scene): CrossfadeSound | null {
    if (!(scene instanceof PaintedScene)) return null;
    const spec = paintedManifestEntry(scene);
    if (!spec) return null;
    const regionKey = resolveRegionKey(spec.music);
    const activeRegion = scene.game.registry.get('painted:active-music-region') as string | undefined;
    const activeSound = scene.game.registry.get('painted:active-music-sound') as CrossfadeSound | undefined;
    if (activeRegion === regionKey && activeSound) {
      const snd = activeSound as unknown as { isPlaying: boolean; pendingRemove: boolean };
      if (snd.isPlaying && !snd.pendingRemove) return activeSound;
    }
    const all = scene.sound.getAll(regionKey);
    for (const s of all) {
      const snd = s as unknown as CrossfadeSound & { isPlaying: boolean; pendingRemove: boolean };
      if (snd.isPlaying && !snd.pendingRemove) return snd;
    }
    return null;
  }

  /**
   * Find the normalised door rect from the edge zone — either the explicit
   * `doorMask` field or the zone's `rect`. Used by threshold-zoom to know
   * the shape of the door opening.
   */
  private findDoorRect(
    req: SceneTransitionRequest,
  ): { x: number; y: number; w: number; h: number } | null {
    if (!req.fromScene) return null;
    const phaserKey = this.resolvePhaserKey(req.fromScene);
    if (!phaserKey) return null;
    const scene = this.game.scene.getScene(phaserKey);
    if (!(scene instanceof PaintedScene)) return null;
    const spec = paintedManifestEntry(scene);
    if (!spec) return null;
    const zone = spec.edgeZones.find((z) => z.targetScene === req.toScene);
    if (!zone) return null;
    if (zone.doorMask) return zone.doorMask;
    return zone.rect ?? null;
  }

  /**
   * Find the door anchor for a doorway transition. The router request
   * doesn't carry the anchor (the manifest associates a door with an
   * interaction by id — `cottage-door` zone ↔ `cottage-door` interaction),
   * so we resolve it from the source scene's manifest entry.
   */
  private findDoorAnchor(req: SceneTransitionRequest): NormalisedPoint | null {
    if (!req.fromScene) return null;
    const phaserKey = this.resolvePhaserKey(req.fromScene);
    if (!phaserKey) return null;
    const scene = this.game.scene.getScene(phaserKey);
    if (!(scene instanceof PaintedScene)) return null;
    const spec = paintedManifestEntry(scene);
    if (!spec) return null;
    const zone = spec.edgeZones.find((z) => z.targetScene === req.toScene);
    if (!zone) return null;
    const interaction = spec.interactions.find(
      (i) => i.id === zone.id && i.anchor !== undefined,
    );
    if (interaction?.anchor) return interaction.anchor;
    if (zone.rect) {
      return {
        x: zone.rect.x + zone.rect.w / 2,
        y: zone.rect.y + zone.rect.h / 2,
      };
    }
    return null;
  }

  /**
   * Run a per-frame callback for `durationMs` ms on the given scene's
   * tween manager. Returns once elapsed reaches the duration.
   *
   * Phaser's `tweens.addCounter` is the right primitive — easing is
   * `Linear` here because per-kind choreography applies its own easing
   * inside the callback.
   */
  private runTimeline(
    scene: Phaser.Scene,
    durationMs: number,
    onUpdate: (elapsedMs: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      let resolved = false;
      const settle = (): void => {
        if (resolved) return;
        resolved = true;
        onUpdate(durationMs);
        resolve();
      };
      const tween = scene.tweens.addCounter({
        from: 0,
        to: durationMs,
        duration: durationMs,
        ease: 'Linear',
        onUpdate: (t: Phaser.Tweens.Tween) => {
          if (resolved) return;
          onUpdate(t.getValue() ?? 0);
        },
        onComplete: settle,
      });
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        try {
          tween.stop();
        } catch {
          /* tween already torn down */
        }
        settle();
      });
    });
  }

  private wait(scene: Phaser.Scene, ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      scene.time.delayedCall(ms, () => resolve());
    });
  }
}

// Re-export the canvas constant so dev wiring can position scenes consistently
// without reaching into private constants.
export { PAINTING_CANVAS_W };
